import * as React from 'react';

import styled, { css } from 'styled-components';

import ErrorBoundary from '@components/ErrorBoundary';
import Slider from '@components/Slider';
import { ResizableImagePlaceholder } from '@containers/ProductDetailContainer/ProductDetail/ProductImageGallery/ImageSlider/ResizableImagePlaceholder';
import { IImageProperties } from '@helpers/utils';

import { youtubeIframe } from '../../../utils';
import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';
import { ModiFaceOverlay } from '../ModiFaceOverlay';

import { ImageSliderWrapper, PlayerWrapper, StyledArrow } from './styled';
import { useTrackSwiping } from './useTrackSwiping';

export interface IImageSliderProps {
  imagesAttrs: IImageProperties[];
  videos?: string[];
  hasModiface?: boolean;
  openModifaceModal?: () => void;
}

const StyledDiv = styled.div<{ newPdDesign: boolean }>`
  ${({ newPdDesign }) =>
    newPdDesign &&
    css`
      &.slick-disabled {
        display: none !important;
      }
    `}
`;

const getSliderSettings = (
  newPdDesign: boolean,
  track: (interaction: 'swipe' | 'button') => void
) => ({
  centerMode: true,
  infinite: false,
  cssEase: 'ease-in-out',
  centerPadding: '0px',
  slidesToShow: 1,
  arrows: true,
  onSwipe: () => track('swipe'),

  prevArrow: (
    <StyledDiv newPdDesign={newPdDesign}>
      <StyledArrow
        direction="left"
        newPdDesign={newPdDesign}
        onClick={() => track('button')}
      />
    </StyledDiv>
  ),
  nextArrow: (
    <StyledDiv newPdDesign={newPdDesign}>
      <StyledArrow
        direction="right"
        newPdDesign={newPdDesign}
        onClick={() => track('button')}
      />
    </StyledDiv>
  ),
});

export const ImageSlider: React.FC<IImageSliderProps> = React.memo(
  ({ imagesAttrs, videos, hasModiface, openModifaceModal }) => {
    const newPdDesign = useNewPdDesignEnabled();
    const { trackSwiping } = useTrackSwiping();

    return (
      <ErrorBoundary>
        <ImageSliderWrapper newPdDesign={newPdDesign}>
          <Slider conf={getSliderSettings(newPdDesign, trackSwiping)}>
            {imagesAttrs.map((imgProps, index) => {
              const showModifaceOverlay = hasModiface && index === 0;

              // if modiface overlay on mobile, clone main image so customer can swipe to image a view it without overlay
              if (showModifaceOverlay) {
                return [
                  <ResizableImagePlaceholder
                    initialInView={true}
                    key={`${imgProps.src}-modiface`}
                    index={index}
                    imgProps={imgProps}
                  >
                    <ModiFaceOverlay onClick={openModifaceModal} />
                  </ResizableImagePlaceholder>,
                  <ResizableImagePlaceholder
                    key={imgProps.src}
                    index={index}
                    imgProps={imgProps}
                  />,
                ];
              }

              return (
                <ResizableImagePlaceholder
                  initialInView={index === 0}
                  key={imgProps.src}
                  index={index}
                  imgProps={imgProps}
                />
              );
            })}

            {videos.map((video) => (
              <PlayerWrapper
                newPdDesign={newPdDesign}
                key={video}
                dangerouslySetInnerHTML={{
                  __html: youtubeIframe(video),
                }}
              />
            ))}
          </Slider>
        </ImageSliderWrapper>
      </ErrorBoundary>
    );
  }
);
